import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, CenterActionCreators, IApplicationState, IBulkCheekSwabDataResponse, IBulkCheekSwabRequest } from '@nkr/store/index';
import { CheekSwabType } from '../../cheek-swab-request-form/cheek-swab-type';
import CheekSwabRequestFormContent from '../../cheek-swab-request-form/form-content';
import { IBaseResponse } from '@nkr/models/index';
import { useEffect, useReducer } from 'react';
import { bulkCheekSwabReducer, initialState, IValidation } from './state';
import { Alert, Button, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CheckSwabRequestRenderType } from '@nkr/views/cheek-swab-request-form';

interface IProps {
    centerId: number,
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterBulkCheekSwabData: (centerId: number) => Promise<IBulkCheekSwabDataResponse>,
    requestCenterBulkCheekSwabs: (centerId: number, request: IBulkCheekSwabRequest) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const BulkCheekSwabRequest = (props: TProps) => {
    const [state, dispatch] = useReducer(bulkCheekSwabReducer, initialState)

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        props.showLoading('Getting mailing info, one moment please...').then(() => {
            props.getCenterBulkCheekSwabData(props.centerId)
                .then(response => {
                    if (response.data.success) {
                        let { firstName, lastName, address1, address2, city, state, postalCode } = response.data.results;

                        dispatch({
                            type: 'after-load',
                            value: {
                                firstName, lastName, address1, address2, city, state, postalCode
                            }
                        });
                    }
                })
                .catch((response) => {
                    toast.error('Unable to complete request: ' + response);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const validate = () => {
        let { firstName, lastName, address1, city, state: stateAbbr, postalCode } = state;

        let validation: IValidation = {
            firstName: firstName != '',
            lastName: lastName != '',
            address1: address1 != '',
            address2: true,
            city: city != '',
            state: stateAbbr != '',
            postalCode: postalCode != '',
        };

        let isValid = validation.firstName && validation.lastName && validation.address1 && validation.city && validation.state && validation.postalCode;

        return { validation, isValid };
    }

    const isValid = () => {
        let { firstName, lastName, address1, city, state: stateAbbr, postalCode } = state.validation;
        return firstName && lastName && address1 && city && stateAbbr && postalCode;
    };

    const handleFieldChange = (name: string, value: any) => {
        dispatch({
            type: 'update-' + name,
            value
        });
    };

    const handleSubmit = () => {
        let { validation, isValid } = validate();

        dispatch({
            type: 'update-from-submit',
            value: {
                validation,
                dirty: true
            }
        });

        if (isValid) {
            props.showLoading('Submitting request, one moment please...').then(() => {
                props.requestCenterBulkCheekSwabs(props.centerId, state)
                    .then(response => {
                        if (response.data.success) {
                            toast.success('Request has been completed.');
                        } else {
                            // Display specific error message from backend (e.g., 24-hour restriction)
                            const errorMessage = (response.data as any).error || response.data.message || 'Unable to complete request.';
                            toast.error(errorMessage);
                        }
                    })
                    .finally(() => {
                        props.hideLoading();
                    });
            });
        }
    };

    return (
        <div className='container-fluid'>
            <h4 style={{ marginBottom: 25 }}>CONFIRM BULK CHEEK SWAB SHIPPING DETAILS</h4>

            <Row>
                <Col xs={12} lg={6}>
                    {isValid() == false && (
                        <Alert bsStyle='danger' bsSize='sm'>The items in red are required...</Alert>
                    )}

                    <CheekSwabRequestFormContent
                        type={CheckSwabRequestRenderType.Center}
                        firstName={state.firstName}
                        lastName={state.lastName}
                        address1={state.address1}
                        address2={state.address2}
                        city={state.city}
                        state={state.state}
                        postalCode={state.postalCode}
                        contactPhone={''}
                        email={''}
                        dob={''}
                        dirty={state.dirty}
                        validation={{ ...state.validation, email: true, contactPhone: true, dob: true }}
                        onChange={handleFieldChange} />
                </Col>
            </Row>

            <Row style={{ paddingTop: 25 }}>
                <Col xs={12} lg={2} lgOffset={2}>
                    <Button block bsStyle='primary' onClick={handleSubmit}>Submit Request</Button>
                </Col>
            </Row>
        </div>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCenterBulkCheekSwabData: (centerId) => {
            return dispatch(CenterActionCreators.getBulkCheekSwabData(centerId));
        },
        requestCenterBulkCheekSwabs: (centerId, request) => {
            return dispatch(CenterActionCreators.requestBulkCheekSwabs(centerId, request));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(BulkCheekSwabRequest);